import { useEffect, useState } from "react";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { httpClient } from "@/httpClient";
import getPharmacyID from "@/utils/pharmacy_code";
import { useRouter } from "@tanstack/react-router";
import { X } from "lucide-react";
import { getPatientDetails } from "../-data/api";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);
import DatePicker from "@/components/ui/date-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { pharmacyClient } from "@/connect";

const EditPatient = ({ patient_id }: { patient_id: string }) => {
  const { toast } = useToast();

  const [patient_name, set_patient_name] = useState("");
  const [patient_phone, set_patient_phone] = useState("");
  const [patient_emirates_id, set_patient_emirates_id] = useState("");
  const [patient_address, set_patient_address] = useState("");
  const [patient_dob, set_patient_dob] = useState<Date | null>(null);
  const [patient_gender, set_patient_gender] = useState<
    "male" | "female" | null
  >(null);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  
  // Add state for original values
  const [originalValues, setOriginalValues] = useState({
    name: "",
    phone: "",
    emirates_id: "",
    address: "",
    dob: null as Date | null,
    gender: null as "male" | "female" | null,
  });

  const router = useRouter();

  // Function to check if any values have changed
  const hasChanges = () => {
    return (
      patient_name !== originalValues.name ||
      patient_phone !== originalValues.phone ||
      patient_emirates_id !== originalValues.emirates_id ||
      patient_address !== originalValues.address ||
      patient_dob?.getTime() !== originalValues.dob?.getTime() ||
      patient_gender !== originalValues.gender
    );
  };

  const SubmitPatientDetails = async () => {
    setLoading(true);

    try {
      if (!patient_name || !patient_phone || !patient_emirates_id) {
        toast({
          title: "Error",
          variant: "destructive",
          description: "Please fill all the fields",
          duration: 5000,
        });
        setLoading(false);
        return;
      }
      await pharmacyClient.updatePatient({
        patientId: patient_id,
        name: patient_name,
        phone: patient_phone,
        govtIdentifier: patient_emirates_id,
        metadata: {
          address: patient_address,
          dateOfBirth: patient_dob ? dayjs(patient_dob).format("YYYY-MM-DD") : "",
          gender: patient_gender!,
        },
      })

      toast({
        title: "Patient Edited",
        variant: "success",
        description: `Patient ${patient_name} has been edited successfully`,
        duration: 5000,
      });
    } catch (error) {
      console.error("Error:", error);
      toast({
        title: "Error",
        variant: "destructive",
        description: "Failed to edit patient. Please try again later.",
        duration: 5000,
      });
    } finally {
      setLoading(false);
      router.invalidate();
      setIsOpen(false);
    }
  };

  // Function to fetch patient data
  const fetchPatientData = async () => {
    try {
      const res = await pharmacyClient.getPatient({
        patientId: patient_id,
      })
      set_patient_name(res.patient?.name!);
      set_patient_phone(res.patient?.phone!);
      set_patient_emirates_id(res.patient?.govtIdentifier!);
      set_patient_address(res.patient?.metadata?.address || "");
      set_patient_gender(res.patient?.metadata?.gender ? res.patient?.metadata.gender as "male" | "female" : null);
      const dob = dayjs(res.patient?.metadata?.dateOfBirth, "YYYY-MM-DD", true).isValid()
        ? dayjs(res.patient?.metadata?.dateOfBirth, "YYYY-MM-DD").toDate()
        : null;
      set_patient_dob(dob);

      // Set original values
      setOriginalValues({
        name: res.patient?.name!,
        phone: res.patient?.phone!,
        emirates_id: res.patient?.govtIdentifier!,
        address: res.patient?.metadata?.address || "",
        dob: dob,
        gender: res.patient?.metadata?.gender ? res.patient?.metadata.gender as "male" | "female" : null,
      });
    } catch (error) {
      console.error("Error:", error);
      toast({
        title: "Error",
        variant: "destructive",
        description: "Failed to get patient details. Please try again later.",
        duration: 5000,
      });
    }
  };

  // Fetch data when component mounts
  useEffect(() => {
    if (patient_id) {
      fetchPatientData();
    }
  }, [patient_id]);

  // Fetch data when sheet is opened
  useEffect(() => {
    if (isOpen && patient_id) {
      fetchPatientData();
    }
  }, [isOpen, patient_id]);

  return (
    <>
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetTrigger asChild>
          <Button variant={"outline"}>
            Edit Patient
          </Button>
        </SheetTrigger>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Edit patient</SheetTitle>
            <SheetDescription>
              The following details are required to edit patient
            </SheetDescription>
            <SheetClose
              className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none disabled:pointer-events-none data-[state=open]:bg-secondary"
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </SheetClose>
          </SheetHeader>
          <p className="text-sm font-semibold mt-4 mb-2">Patient Name</p>
          <Input
            placeholder="Patient Name"
            value={patient_name}
            onChange={(e) => set_patient_name(e.target.value)}
          />
          <p className="text-sm font-semibold mt-4 mb-2">Phone Number</p>
          <Input
            placeholder="Phone Number"
            type="number"
            value={patient_phone}
            onChange={(e) => set_patient_phone(e.target.value)}
          />
          <p className="text-sm font-semibold mt-4 mb-2">Govt. Identifier</p>
          <Input
            placeholder="Patient Govt. Identifier"
            value={patient_emirates_id}
            onChange={(e) => set_patient_emirates_id(e.target.value)}
          />
          <p className="text-sm font-semibold mt-4 mb-2">Address</p>
          <Input
            placeholder="Patient Address"
            value={patient_address}
            onChange={(e) => set_patient_address(e.target.value)}
          />
          <p className="text-sm font-semibold mt-4 mb-2">Date of Birth</p>
          <DatePicker
            onSelect={(date) => set_patient_dob(date!)}
            selected={patient_dob!}
            maxDate={new Date()}
          />
          <p className="text-sm font-semibold mt-4 mb-2">Gender</p>
          <Select
            onValueChange={(value: string) => {
              set_patient_gender(value as "male" | "female");
            }}
            value={patient_gender!}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select Discount Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="male">Male</SelectItem>
              <SelectItem value="female">Female</SelectItem>
            </SelectContent>
          </Select>
          {/* <hr className='my-4'/> */}
          <Button
            className="absolute bottom-2 right-2"
            onClick={SubmitPatientDetails}
            disabled={loading || !hasChanges()}
          >
            {loading ? "Loading..." : "Update Changes"}
          </Button>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default EditPatient;
